/**
 * @author arodic / https://github.com/arodic
 */
'use strict';

// TODO: Fix warnings by refactor away the Gizmo objects from THREE.
// (Not used outside of this file)

import { t } from 'i18next';
import * as THREE from 'three';
import { Euler, Object3D, Quaternion, Vector2, Vector3 } from 'three';

import {
  ContextMenuItem,
  SkyViewContextMenuProps,
} from '../../react/components/skyview/context-menu';
import { rotateObjectAroundPivotPoint } from '../../utils/three/rotate-object-around-pivot-point';
import { isDefined } from '../../utils/variables';
import CameraManager from '../camera-manager';
import { saveCurrentTransformation } from '../elements/components/dynamic-asset-file-version-transform';
import { ElementObj } from '../elements/element-obj';
import { Geometry } from '../elements/geometry';
import { Glb } from '../elements/glb';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import {
  getPerpendicularVectorInDirection,
  getQuaternionBetweenVectors,
} from '../utilities/math-helpers';
import { Pick } from '../utilities/pick';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';
import { option } from './utils/context-menu/commands';
import { GizmoRotate } from './utils/gizmos/gizmo-rotate';
import { GizmoScale } from './utils/gizmos/gizmo-scale';
import { GizmoTranslate } from './utils/gizmos/gizmo-translate';

type Axis = 'XYZE' | 'XYZ' | 'E' | 'X' | 'Y' | 'Z' | '';
export type TransformMode = 'translate' | 'rotate' | 'scale';

export function getTransformModeFreezeKey(transformMode: TransformMode) {
  const keyMap = {
    rotate: 'rotateZ',
    translate: 'translate',
    scale: 'scale',
  } as const;

  return keyMap[transformMode];
}

// Yellow
const defaultCursorColor = 0xffff00;
/**
 * Multiplier for gizmo size.
 * It is calibrated so the gizmo does not appear too small or too big in the viewer.
 */
const gizmoScaleMultiplier = 0.11667;

export default class TransformTool extends Tool {
  private _mode: TransformMode = 'translate';
  private _dragging = false;
  private readonly _gizmo = {
    translate: new GizmoTranslate(),
    rotate: new GizmoRotate(),
    scale: new GizmoScale(),
  };

  /**
   * The last intersection point. Is updated when the object is transformed or when mouse down is pressed.
   */
  lastPlaneIntersectionPoint = new Vector3();
  axis: Axis;

  lastAssetIntersectionPoint: Vector3 | undefined;

  asset?: ElementObj;

  /**
   * The local point on the asset where the transformation is taking place.
   */
  localObjectTransformPoint = new Vector3();

  /**
   * When true it will only allow rotation in steps of 90 degrees.
   */
  snapRotationTo90Degrees = false;

  ray = new THREE.Raycaster();
  cursorPoint: PickingSphere;
  geometryPointIndex: number | undefined;

  contextMenuCallback?: (contextMenu?: SkyViewContextMenuProps) => void = undefined;

  constructor(inputHandler: InputHandler) {
    super('transform', inputHandler);

    this.asset = undefined;
    this.axis = '';
    this.visible = false;

    this.cursorPoint = new PickingSphere(new THREE.Color(defaultCursorColor));
    this.add(this.cursorPoint);

    this.add(this._gizmo.rotate);
    this.add(this._gizmo.translate);
    this.add(this._gizmo.scale);
    this.disable();
  }

  enable() {
    this._gizmo.rotate.visible = this._mode === 'rotate';
    this._gizmo.translate.visible = this._mode === 'translate';
    this._gizmo.scale.visible = this._mode === 'scale';
  }

  disable() {
    this._gizmo.rotate.visible = false;
    this._gizmo.translate.visible = false;
    this._gizmo.scale.visible = false;
  }

  attachAsset(object: ElementObj) {
    this.asset = object;
    this.visible = true;

    const isVerticalRotationEnabled = !this.asset.freeze.rotateXY;
    this._gizmo.rotate.pickerGizmos!.X[0][0].visible = isVerticalRotationEnabled;
    this._gizmo.rotate.handleGizmos!.X[0][0].visible = isVerticalRotationEnabled;

    this._gizmo.rotate.pickerGizmos!.Y[0][0].visible = isVerticalRotationEnabled;
    this._gizmo.rotate.handleGizmos!.Y[0][0].visible = isVerticalRotationEnabled;

    this._gizmo.rotate
      .pickers!.children.filter((x) => x.name !== 'Z')
      .forEach((x) => (x.visible = isVerticalRotationEnabled));

    this.centerTransformPoint();
    this.update();
  }

  detach() {
    this.asset = undefined;
    this.visible = false;
    this.axis = '';
    this.geometryPointIndex = undefined;
  }

  onProjectProjectionUpdated() {
    this.refreshPosition();
    return true;
  }

  /**
   * Aborts the transform tool if another asset is selected.
   * This is important to ensure that the wrong asset isn't moved.
   */
  onNewAssetSelection(asset: ElementObj) {
    if (asset.uuid !== this.asset?.uuid) {
      this.inputHandler.setTool('select');
    }
  }

  /**
   * Refreshes the position of the tool.
   */
  refreshPosition() {
    if (isDefined(this.asset)) {
      if (this.asset instanceof Geometry && isDefined(this.geometryPointIndex)) {
        this.position
          .copy(this.asset.position)
          .add(
            this.asset.getLocalPoints().map((x) => x.point)[this.geometryPointIndex] ??
              new Vector3(0, 0, 0),
          );
      } else {
        this.position.copy(this.asset.localToWorld(this.localObjectTransformPoint.clone()));
      }
    }
  }

  /**
   * Centers the transformation point to the center of the object.
   */
  centerTransformPoint() {
    if (!isDefined(this.asset)) {
      return;
    }

    const center = this.asset.generateBoundingBox(true).getCenter(new Vector3());
    this.localObjectTransformPoint = this.asset.worldToLocal(center);
  }

  getMode() {
    return this._mode;
  }

  setMode(mode: TransformMode) {
    this._mode = mode ? mode : this._mode;
    this.enable();
    this.update();
  }

  update() {
    const camera = CameraManager().getActiveCamera();
    if (!isDefined(this.asset) || !isDefined(camera)) {
      return;
    }

    const threeCamera = camera.getTHREECamera();
    this.asset.matrixWorldNeedsUpdate = true;
    this.asset.updateMatrixWorld();

    threeCamera.updateMatrixWorld();

    this.refreshPosition();

    // Calculate the scale of the gizmo based on how close we are to it.
    const gizmoScale = camera.getDistanceToPoint(this.position) * gizmoScaleMultiplier;
    this.scale.set(gizmoScale, gizmoScale, gizmoScale);

    const gizmoRotation = new Euler();

    // Rotate the gizmo if it is in rotation mode except.
    if (this.getMode() === 'rotate' && !(this.asset instanceof Glb && this.asset.canBeScaled())) {
      gizmoRotation.setFromRotationMatrix(this.asset.matrixWorld);
    }

    this.ray.setFromCamera(new THREE.Vector2(0, 0), threeCamera);
    this._gizmo[this._mode].update(gizmoRotation, this.ray.ray.direction);
  }

  updatePickingPlane(e: MouseInputData) {
    if (!isDefined(this.asset) || this._dragging) {
      return false;
    }

    const intersect = this.intersectObjects(
      e.pointer.position,
      this._gizmo[this._mode].pickers!.children.filter((x) => x.visible),
    );
    if (!intersect) {
      this._gizmo[this._mode].highlight('');
      return false;
    }

    if (this.axis !== intersect.object.name) {
      this.axis = intersect.object.name as Axis;
      this.update();
    }

    this._gizmo[this._mode].highlight(this.axis);
    return true;
  }

  onTouchStart(e: any) {
    // Refactor for touches
    //this.onMouseDown(e);
    return true;
  }

  /**
   * Updates the geometry transformation point. Only has effect it the currently
   * transformed object is of type Geometry.
   */
  updateGeometryTransformationPoint(e: MouseInputData) {
    if (!(this.asset instanceof Geometry)) {
      this.geometryPointIndex = undefined;
      return;
    }

    const skyviewCamera = CameraManager().getActiveCamera();
    if (!skyviewCamera) {
      return;
    }

    const pickedIndex = this.asset.pickTransformationPointIndex(skyviewCamera, e);
    if (isDefined(pickedIndex)) {
      this.geometryPointIndex = pickedIndex;
      return;
    }

    const intersectsAsset = this.intersectObjects(e.pointer.position, [this.asset]);
    if (intersectsAsset === false) {
      return;
    }

    this.centerTransformPoint();
    this.geometryPointIndex = undefined;
  }

  onMouseDown(e: MouseInputData) {
    const camera = CameraManager().getActiveCamera()?.getTHREECamera();
    if (!isDefined(camera)) {
      return true;
    }

    this.mousePosition = e.pointer.client;
    if (
      this.asset === undefined ||
      this._dragging === true ||
      (e.button !== undefined && e.button.type !== MouseButtonType.LeftButton)
    ) {
      return true;
    }

    if (e.button.type === MouseButtonType.LeftButton) {
      const intersect = this.intersectObjects(
        e.pointer.position,
        this._gizmo[this._mode].pickers!.children.filter((x) => x.visible),
      );
      if (intersect) {
        this.axis = intersect.object.name as Axis;
        this.update();
        this.updateActiveGizmoPlane();

        const planeIntersect = this.intersectObjects(e.pointer.position, [
          this._gizmo[this._mode].activePlane!,
        ]);
        if (planeIntersect) {
          this.lastPlaneIntersectionPoint.copy(planeIntersect.point);
        }
      } else {
        this.updateGeometryTransformationPoint(e);
        this.refreshPosition();
      }

      if (intersect) {
        this._dragging = true;
        return false;
      }
    }

    return true;
  }

  onTouchMove(e: any) {
    // Refactor for touches
    //return this.onMouseMove(e);
    return true;
  }

  onMouseMove(e: MouseInputData) {
    if (this.mouseMoved(e)) {
      this.contextMenuCallback?.();
    }

    this.updatePickingPlane(e);

    this.lastAssetIntersectionPoint = Pick.instance.assetPoint(
      e.pointer.position.x,
      e.pointer.position.y,
      (asset) => asset.uuid === this.asset?.uuid,
    );

    this.cursorPoint.visible = isDefined(this.lastAssetIntersectionPoint);

    if (isDefined(this.lastAssetIntersectionPoint)) {
      this.cursorPoint.position.copy(this.worldToLocal(this.lastAssetIntersectionPoint.clone()));
    }

    if (!isDefined(this.asset)) {
      return true;
    }

    const planeIntersect = this.intersectObjects(e.pointer.position, [
      this._gizmo[this._mode].activePlane!,
    ]);

    if (planeIntersect === false || !this._dragging || this.axis === '') {
      return true;
    }

    const planeIntersectionPoint = planeIntersect.point.clone();
    let isAssetTransformed = false;

    if (this._mode === 'translate' && !this.asset.freezeState.translate) {
      if (this.asset instanceof Geometry && isDefined(this.geometryPointIndex)) {
        isAssetTransformed = translateGeometryPoint(
          this.asset,
          this.lastPlaneIntersectionPoint,
          planeIntersectionPoint,
          this.axis,
          this.geometryPointIndex,
        );
      } else {
        isAssetTransformed = translateObject(
          this.asset,
          this.lastPlaneIntersectionPoint,
          planeIntersectionPoint,
          this.axis,
        );
      }
    } else if (this._mode === 'scale' && !this.asset.freezeState.scale) {
      isAssetTransformed = scaleObject(
        this.asset,
        this.lastPlaneIntersectionPoint,
        planeIntersectionPoint,
        this.axis,
      );
    } else if (this._mode === 'rotate' && !this.asset.freezeState.rotateZ) {
      isAssetTransformed = rotateObject(
        this.asset,
        this.position.clone(),
        this.lastPlaneIntersectionPoint,
        planeIntersectionPoint,
        this.snapRotationTo90Degrees || this.inputHandler.shiftIsDown,
      );
    }

    // Copy the new intersection point to the last plane intersection point so the next
    // time this function is run the angle can be calculated based on the new movement when rotating.
    if (isAssetTransformed) {
      this.lastPlaneIntersectionPoint.copy(planeIntersectionPoint);
      // Indicate to the object that is has been transformed
      this.asset?.onTransformed();
      this.update();
    }

    return true;
  }

  onTouchEnd(e: any) {
    // Refactor fortouches
    // return this.onMouseUp(e);
    return true;
  }

  async onMouseUp(e: MouseInputData) {
    if (!this.mouseMoved(e) && e.button.type === MouseButtonType.RightButton) {
      this.updateContextMenu(e);
      return true;
    }

    if (this._dragging && this.axis !== null) {
      this.updateActiveGizmoPlane();
    }

    if (
      e.button.type === MouseButtonType.LeftButton &&
      this._dragging &&
      (this._gizmo[this._mode].activePlane || this._gizmo[this._mode].axis)
    ) {
      this._dragging = false;
      await this.saveTransformation();
    }

    this._dragging = false;
    return true;
  }

  updateActiveGizmoPlane() {
    const camera = CameraManager().getActiveCamera()?.getTHREECamera();
    if (!camera) {
      return false;
    }

    this.ray.setFromCamera(new Vector2(0, 0), camera);
    this._gizmo[this._mode].setActivePlane(this.axis, this.ray.ray.direction);
  }

  onMouseWheel(e: any) {
    this.update();
    return true;
  }

  intersectObjects(position: THREE.Vector2, objects: Object3D[]) {
    const camera = CameraManager().getActiveCamera()?.getTHREECamera();
    if (!camera) {
      return false;
    }

    this.ray.setFromCamera(position, camera);

    const intersections = this.ray.intersectObjects(objects, true);
    return intersections[0] ? intersections[0] : false;
  }

  onContextMenuChanged(callback: (contextMenu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }
  clearContextMenu() {
    this.contextMenuCallback?.();
  }

  async saveTransformation() {
    if (!isDefined(this.asset)) {
      return;
    }

    try {
      await saveAssetTransformation(this.asset);
    } catch {
      // TODO: If saving fails then we should revert to the original position.
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: t('unableToSave', { ns: 'skyviewScale' }),
      });
    }
  }

  updateContextMenu(e: MouseInputData) {
    const items: ContextMenuItem[] = [
      {
        title: t('transformTool.contextMenu.setOrigin', { ns: 'skyview' }),
        action: () => {
          if (!isDefined(this.asset)) {
            return;
          }

          this.localObjectTransformPoint.copy(
            this.asset.worldToLocal(this.lastAssetIntersectionPoint!.clone()),
          );
          this.cursorPoint.visible = false;
          this.lastAssetIntersectionPoint = undefined;
          this.update();
          this.contextMenuCallback?.();
        },
        disabled: !isDefined(this.lastAssetIntersectionPoint) || this.asset instanceof Geometry,
      },
      {
        title: t('transformTool.contextMenu.centerOrigin', { ns: 'skyview' }),
        action: () => {
          this.centerTransformPoint();
          this.cursorPoint.visible = false;
          this.lastAssetIntersectionPoint = undefined;
          this.update();
          this.contextMenuCallback?.();
        },
        disabled: this.asset instanceof Geometry,
      },
      ...option(this.getMode() === 'translate', () => {
        return {
          title: t('transformTool.contextMenu.moveToNewCoordinate', { ns: 'skyview' }),
          action: () => {
            if (!isDefined(this.asset)) {
              return;
            }

            publish(SubscriptionTopic.ShowSetAssetCoordinateDialog, {
              asset: this.asset,
              originWorldPosition: this.position.clone(),
            });
            this.contextMenuCallback?.();
          },
        };
      }),
      ...option(this.getMode() === 'rotate', () => {
        return {
          title: t('transformTool.contextMenu.resetRotation', { ns: 'skyview' }),
          action: async () => {
            if (!isDefined(this.asset)) {
              return;
            }

            const quaternion = this.asset.getWorldQuaternion(new Quaternion()).invert();
            rotateObjectAroundPivotPoint(this.asset, this.position.clone(), quaternion);
            this.asset?.rotation.setFromVector3(new Vector3(0, 0, 0));
            await this.saveTransformation();
            this.contextMenuCallback?.();
          },
        };
      }),
      ...option(this.getMode() === 'rotate', () => {
        return {
          title: t('transformTool.contextMenu.prependicularSnap', { ns: 'skyview' }),
          hotkey: 'Shift',
          toggled: this.snapRotationTo90Degrees,
          action: () => {
            this.snapRotationTo90Degrees = !this.snapRotationTo90Degrees;
          },
        };
      }),
      {
        title: t('cancel', { ns: 'common' }),
        hotkey: 'Esc',
        action: () => {
          this.inputHandler.setTool('select');
          this.contextMenuCallback?.();
        },
      },
    ];

    this.contextMenuCallback?.({
      pos: {
        x: e.viewportPos?.clientX ?? 0,
        y: e.viewportPos?.clientY ?? 0,
      },
      items,
    });
  }
}

function scaleObject(
  _objectToScale: Object3D,
  _lastPoint: Vector3,
  _newPoint: Vector3,
  _axis: Axis,
) {
  // TODO: Not implemented
  return true;
}

function translateObject(
  objectToTranslate: Object3D,
  lastPoint: Vector3,
  newPoint: Vector3,
  axis: Axis,
) {
  const diff = newPoint.clone().sub(lastPoint);
  objectToTranslate.position.add(getVectorByAxis(diff, axis));
  return true;
}

function translateGeometryPoint(
  geometry: Geometry,
  lastPoint: Vector3,
  newPoint: Vector3,
  axis: Axis,
  pointIndex: number,
) {
  const diff = newPoint.clone().sub(lastPoint);
  geometry.getLocalPoints()[pointIndex]?.point.add(getVectorByAxis(diff, axis));
  geometry.createGeometry();
  return true;
}

function getVectorByAxis(v: Vector3, axis: Axis) {
  return new Vector3(
    axis.includes('X') ? v.x : 0,
    axis.includes('Y') ? v.y : 0,
    axis.includes('Z') ? v.z : 0,
  );
}

/**
 * Gets the vectors between the pivotPoint and the last and the new point and calculates the quaternion between them.
 * Then rotates the object and repositions it so that it will be rotated correctly around that pivot point.
 * @param objectToRotate
 * @param pivotPoint
 * @param lastPoint
 * @param newPoint
 */
function rotateObject(
  objectToRotate: Object3D,
  pivotPoint: Vector3,
  lastPoint: Vector3,
  newPoint: Vector3,
  snapRotationTo90Degrees: boolean,
) {
  const v1 = lastPoint.clone().sub(pivotPoint);
  const v2 = newPoint.clone().sub(pivotPoint);

  if (snapRotationTo90Degrees && !vectorAngleExceedsPerpendicularSnapTreshold(v1, v2)) {
    return false;
  }

  if (snapRotationTo90Degrees) {
    v2.copy(getPerpendicularVectorInDirection(v1, v2));
  }

  const quaternion = getQuaternionBetweenVectors(v1, v2);
  rotateObjectAroundPivotPoint(objectToRotate, pivotPoint, quaternion);
  return true;
}

/**
 * Checks if the angle between the vectors is closer to being perpendicular or closer to be zero.
 */
function vectorAngleExceedsPerpendicularSnapTreshold(v1: Vector3, v2: Vector3) {
  // Normalize the vectors
  v1.normalize();
  v2.normalize();

  // Calculate the dot product
  const dotProduct = v1.dot(v2);

  return Math.abs(dotProduct - 1) >= Math.abs(dotProduct);
}

export async function saveAssetTransformation(asset: ElementObj) {
  if (asset instanceof Glb) {
    await saveCurrentTransformation(asset);
  }

  await asset.onChange();
}
